"use client";

import { useId, useState } from "react";
import Link from "next/link";
import { announce } from "@/lib/announce";
import { trackGuideCarCheck } from "@/lib/tracking";
import { TESLA_FACTS } from "@/lib/tesla-facts";
import { checkMyCar, yearsForModel, type CarModel } from "@/lib/check-my-car";
import { inlineTouchLinkClass } from "@/lib/guide-ui";

interface CheckMyCarProps {
  models: CarModel[];
  /** Newest model year to offer. Passed from the server so the list is stable per render. */
  latestYear: number;
}

/**
 * "Check my car": model + model year in, one verdict line and the in-car check
 * out. It sits at the top of /guide so the question every visitor has (will
 * this work on my car?) is answered before the steps.
 *
 * Without JavaScript the block shows the site-wide compatibility statement, so
 * the answer is never empty. The verdict is text, not colour, and is announced
 * through the shared live region (lib/announce.ts).
 */
export default function CheckMyCar({ models, latestYear }: CheckMyCarProps) {
  const [modelKey, setModelKey] = useState<string>("");
  const [year, setYear] = useState<string>("");
  const yearId = useId();

  const model = models.find(m => m.key === modelKey);
  const years = model ? yearsForModel(model, latestYear) : [];
  const verdict = model && year ? checkMyCar(model, Number(year)) : null;

  const pickModel = (key: string) => {
    setModelKey(key);
    setYear("");
  };

  const pickYear = (value: string) => {
    setYear(value);
    if (!model || !value) return;
    const answer = checkMyCar(model, Number(value));
    announce(answer.headline, "polite");
    trackGuideCarCheck({ model: model.key, model_year: Number(value), verdict: answer.status });
  };

  return (
    <div
      className="py-6"
      style={{
        borderTop: "1px solid var(--tls-border-subtle)",
        borderBottom: "1px solid var(--tls-border-subtle)",
      }}
    >
      <p className="tls-h4 mb-3">Check my car</p>
      <p className="text-sm text-[var(--tls-fg-muted)]">
        Will it work on your Tesla? Pick a model and a model year.
      </p>

      {/* eslint-disable-next-line react-doctor/prefer-tag-over-role -- role=group on a toolbar div; no semantic HTML element maps to a button-group toolbar */}
      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Tesla model">
        {models.map(m => (
          <button
            key={m.key}
            type="button"
            onClick={() => pickModel(m.key)}
            aria-pressed={m.key === modelKey}
            className={`tlsr-pill tlsr-focus${m.key === modelKey ? " is-active" : ""}`}
          >
            {m.name}
          </button>
        ))}
      </div>

      <label htmlFor={yearId} className="mt-4 block text-sm font-medium text-[var(--tls-fg-strong)]">
        Model year
      </label>
      <select
        id={yearId}
        value={year}
        disabled={!model}
        onChange={event => pickYear(event.target.value)}
        className="tlsr-focus mt-2 w-full rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] px-3.5 text-base text-[var(--tls-fg-strong)] disabled:opacity-50"
        style={{ height: "var(--tls-field-h)" }}
      >
        <option value="">{model ? "Select a year" : "Pick a model first"}</option>
        {years.map(y => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>

      <div className="mt-5 border-t border-[color:var(--tls-border-subtle)] pt-5">
        {verdict && model ? (
          <>
            <p className="font-display text-lg font-semibold leading-snug text-[var(--tls-fg-strong)]">
              {verdict.headline}
            </p>
            <p className="mt-2 text-sm text-[var(--tls-fg-muted)]">{verdict.support}</p>
            <p className="mt-3 text-sm leading-relaxed text-[var(--tls-fg-muted)]">{verdict.confirm}</p>
            <p className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-sm">
              <Link href="#install" className={`${inlineTouchLinkClass} font-medium`}>
                Install steps
              </Link>
              <Link href={verdict.guideHref} className={inlineTouchLinkClass}>
                {model.name} guide
              </Link>
            </p>
          </>
        ) : (
          <>
            <p className="text-sm leading-relaxed text-[var(--tls-fg-muted)]">
              {TESLA_FACTS.compatibility.short}.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-[var(--tls-fg-muted)]">
              {TESLA_FACTS.compatibility.inCarCheck}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
